import { FC, useCallback, useContext, useEffect, useState } from "react";
import { animated, useTransition } from "react-spring";

import { DesignTokenProps, createComponentStyleHook } from "../../tokens";

import { Alert } from "../Alert";
import { AlertContext, AlertData } from "./AlertContext";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The alert container properties.
 */
export interface AlertContainerProps extends DesignTokenProps {
  /**
   * The current alert data.
   */
  readonly currentAlert: AlertData | null;

  /** Indicates whether the container belongs to a root alert container. */
  readonly root: boolean;
}

const useResolvedAlertContainerStyles = createComponentStyleHook<AlertContainerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "alert",
});

/**
 * The alert container component.
 */
export const AlertContainer: FC<AlertContainerProps> = (props) => {
  const { currentAlert, root } = props;
  const [activeAlert, setActiveAlert] = useState<AlertData | null>(null);
  const { mode, styles, StyleModeBoundary } = useResolvedAlertContainerStyles(props);

  const transitions = useTransition(activeAlert ? [activeAlert] : [], {
    from: { opacity: 0, translateY: "-100%" },
    enter: { opacity: 1, translateY: "0%" },
    leave: { opacity: 0, translateY: "-100%" },
    config: { duration: 200 },
  });

  const { hideAlert } = useContext(AlertContext);

  useEffect(() => {
    setActiveAlert(currentAlert);

    const timer = setTimeout(() => {
      hideAlert();
    }, currentAlert?.timeout || 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [currentAlert]);

  const dialogRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (ref) ref.showPopover();
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      {transitions((style, alert) => (
        <animated.div
          {...{ popover: "manual" }}
          ref={dialogRefHandler}
          className={styles.alertContainer}
          data-testid={root ? "test__alert-container" : undefined}
          style={style}
        >
          <Alert message={alert.message} variant={alert.variant ?? alert.style ?? "banner"} type={alert.type} root={root} />
        </animated.div>
      ))}
    </StyleModeBoundary>
  );
};
